import store from '@/store';

export interface ICartItem {
  Id: number;
  Path: string;
  Url: string;
  Data: any;
}

export class PdfExportService {
  /**
   * Generate and download a PDF from cart items
   */
  static async generatePdf(): Promise<void> {
    const { jsPDF } = await import('jspdf');
    const autoTable = (await import('jspdf-autotable')).default;
    
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      putOnlyUsedFonts: true,
      compress: true
    }) as any;

    // Enable PDF/UA compatibility mode
    pdf.internal.events.subscribe('postPutResources', function () {
      // Set document language for accessibility
      pdf.internal.write('/Lang (sv-SE)');
      
      // Mark as tagged PDF (basic implementation)
      pdf.internal.write('/MarkInfo << /Marked true >>');
    });

    // Set PDF metadata for accessibility
    pdf.setProperties({
      title: 'Fysisk tillgänglighet – sammanfattande riktlinjer',
      subject: 'Exporterade riktlinjer från kundvagnen',
      author: 'VGR Riktlinjer',
      keywords: 'riktlinjer, export, pdf, tillgänglighet',
      creator: 'VGR Riktlinjer System',
      lang: 'sv-SE'
    });

    let yPosition = 20;
    const pageHeight = 297;
    const marginLeft = 15;
    const marginRight = 15;
    const pageWidth = 210;
    const contentWidth = pageWidth - marginLeft - marginRight;

    // Function to add page numbers
    const addPageNumbers = () => {
      const pageCount = (pdf as any).internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Sida ${i} av ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
      }
    };

    // Array to store bookmark data and structure elements
    const bookmarks: { title: string; page: number; y: number }[] = [];
    const structureTree: any[] = [];

    // Document Title (H1 level)
    pdf.setFontSize(20);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Fysisk tillgänglighet – sammanfattande riktlinjer', marginLeft, yPosition);
    structureTree.push({ type: 'H1', text: 'Fysisk tillgänglighet – sammanfattande riktlinjer', page: 1, y: yPosition });
    yPosition += 15;

    // Group cart items by Path
    const groupedItems: { [key: string]: any[] } = {};
    for (const cartItem of store.state.cart as ICartItem[]) {
      const key = cartItem.Path || 'Other';
      if (!groupedItems[key]) {
        groupedItems[key] = [];
      }
      groupedItems[key].push(cartItem);
    }

    // Process each group
    for (const [path, items] of Object.entries(groupedItems)) {
      // Save bookmark for this section
      const currentPage = pdf.internal.getCurrentPageInfo().pageNumber;
      bookmarks.push({ title: path, page: currentPage, y: yPosition });

      // Section heading (H2 level)
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      const pathLines = pdf.splitTextToSize(path, contentWidth);
      pdf.text(pathLines, marginLeft, yPosition);
      structureTree.push({ type: 'H2', text: path, page: currentPage, y: yPosition });
      yPosition += pathLines.length * 8;

      // Section link (once per group, use first item's URL)
      if (items[0].Url) {
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(0, 0, 255);
        const baseUrl = window.location.origin;
        const fullUrl = baseUrl + items[0].Url;
        const urlLines = pdf.splitTextToSize(fullUrl, contentWidth);
        for (let i = 0; i < urlLines.length; i++) {
          pdf.textWithLink(urlLines[i], marginLeft, yPosition, { url: fullUrl });
          yPosition += 4;
        }
        pdf.setTextColor(0, 0, 0);
        yPosition += 2;
      }

      yPosition += 3;

      // Process each item in the group
      for (const cartItem of items) {
        const estimatedItemHeight = 20;
        
        // Check if we need a new page
        if (yPosition > pageHeight - estimatedItemHeight) {
          pdf.addPage();
          yPosition = 20;
        }

        // Item name (H3 level)
        const itemPage = pdf.internal.getCurrentPageInfo().pageNumber;
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        const nameLines = pdf.splitTextToSize(cartItem.Data.Name, contentWidth);
        pdf.text(nameLines, marginLeft, yPosition);
        structureTree.push({ type: 'H3', text: cartItem.Data.Name, page: itemPage, y: yPosition });
        yPosition += nameLines.length * 6 + 3;

        // Description
        if (cartItem.Data.Description) {
          pdf.setFontSize(9);
          pdf.setFont('helvetica', 'normal');
          const cleanDesc = PdfExportService.stripHtml(cartItem.Data.Description);
          const descLines = pdf.splitTextToSize(cleanDesc, contentWidth);
          pdf.text(descLines, marginLeft, yPosition);
          yPosition += descLines.length * 5 + 3;
        }

        // Information
        if (cartItem.Data.Information) {
          pdf.setFontSize(9);
          pdf.setFont('helvetica', 'italic');
          const cleanInfo = PdfExportService.stripHtml(cartItem.Data.Information);
          const infoLines = pdf.splitTextToSize('Syfte: ' + cleanInfo, contentWidth);
          pdf.text(infoLines, marginLeft, yPosition);
          yPosition += infoLines.length * 5 + 3;
        }

        // Text
        if (cartItem.Data.Text) {
          pdf.setFontSize(9);
          pdf.setFont('helvetica', 'normal');
          const cleanText = PdfExportService.stripHtml(cartItem.Data.Text);
          const textLines = pdf.splitTextToSize(cleanText, contentWidth);
          pdf.text(textLines, marginLeft, yPosition);
          yPosition += textLines.length * 5 + 3;
        }

        // Guideline items
        if (cartItem.Data.GuidelineItems && cartItem.Data.GuidelineItems.length > 0) {
          for (const item of cartItem.Data.GuidelineItems) {
            if (yPosition > pageHeight - 25) {
              pdf.addPage();
              yPosition = 20;
            }

            // Guideline item name (H4 level)
            const guidelineItemPage = pdf.internal.getCurrentPageInfo().pageNumber;
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'bold');
            const itemNameLines = pdf.splitTextToSize(item.Name, contentWidth);
            pdf.text(itemNameLines, marginLeft + 5, yPosition);
            structureTree.push({ type: 'H4', text: item.Name, page: guidelineItemPage, y: yPosition });
            yPosition += itemNameLines.length * 5 + 2;

            // Guideline item description (dialog information)
            if (item.Description) {
              pdf.setFontSize(9);
              pdf.setFont('helvetica', 'italic');
              const itemDescLines = pdf.splitTextToSize('Information: ' + PdfExportService.stripHtml(item.Description), contentWidth - 5);
              pdf.text(itemDescLines, marginLeft + 5, yPosition);
              yPosition += itemDescLines.length * 4 + 2;
            }

            // Guideline item text
            if (item.Text) {
              pdf.setFontSize(9);
              pdf.setFont('helvetica', 'normal');
              const itemTextLines = pdf.splitTextToSize(PdfExportService.stripHtml(item.Text), contentWidth - 5);
              pdf.text(itemTextLines, marginLeft + 5, yPosition);
              yPosition += itemTextLines.length * 5 + 2;
            }

            // Conditions in table format
            const extCondition = item.Extended?.Condition ? PdfExportService.stripHtml(item.Extended.Condition) : '';
            const normCondition = item.Normal?.Condition ? PdfExportService.stripHtml(item.Normal.Condition) : '';
            
            if (extCondition || normCondition) {
              // Check if we need a new page for the table
              if (yPosition > pageHeight - 35) {
                pdf.addPage();
                yPosition = 20;
              }

              const tableData: any[] = [];
              
              // If conditions are the same, create a single row with split cell
              if (extCondition && normCondition && extCondition === normCondition) {
                // Single row with merged text cell
                tableData.push([
                  '', // Empty cell that will be custom drawn
                  extCondition
                ]);
              } else {
                // Different conditions
                if (extCondition) {
                  tableData.push([
                    { content: 'Grön', styles: { fillColor: [103, 191, 106], textColor: [0, 0, 0], fontStyle: 'bold' as any } }, 
                    extCondition
                  ]);
                }
                if (normCondition) {
                  tableData.push([
                    { content: 'Gul', styles: { fillColor: [255, 193, 7], textColor: [0, 0, 0], fontStyle: 'bold' as any } }, 
                    normCondition
                  ]);
                }
              }

              autoTable(pdf, {
                startY: yPosition,
                margin: { left: marginLeft + 5 },
                tableWidth: contentWidth - 5,
                head: [],
                body: tableData,
                theme: 'grid',
                styles: {
                  fontSize: 9,
                  cellPadding: 2,
                  lineColor: [200, 200, 200],
                  lineWidth: 0.1
                },
                columnStyles: {
                  0: { cellWidth: 25, halign: 'center', valign: 'middle' },
                  1: { cellWidth: 'auto' }
                },
                didDrawCell: function(data: any) {
                  // Draw split cell for same conditions
                  if (data.section === 'body' && data.column.index === 0 && 
                      extCondition && normCondition && extCondition === normCondition) {
                    const cellX = data.cell.x;
                    const cellY = data.cell.y;
                    const cellWidth = data.cell.width;
                    const cellHeight = data.cell.height;
                    const halfWidth = cellWidth / 2;
                    
                    // Draw green left half
                    pdf.setFillColor(103, 191, 106);
                    pdf.rect(cellX, cellY, halfWidth, cellHeight, 'F');
                    
                    // Draw yellow right half
                    pdf.setFillColor(255, 193, 7);
                    pdf.rect(cellX + halfWidth, cellY, halfWidth, cellHeight, 'F');
                    
                    // Add text
                    pdf.setTextColor(0, 0, 0);
                    pdf.setFontSize(9);
                    pdf.setFont('helvetica', 'bold');
                    pdf.text('Grön', cellX + halfWidth / 2, cellY + cellHeight / 2 + 1, { align: 'center' });
                    
                    pdf.text('Gul', cellX + halfWidth + halfWidth / 2, cellY + cellHeight / 2 + 1, { align: 'center' });
                    
                    // Reset text color
                    pdf.setTextColor(0, 0, 0);
                    
                    // Redraw cell border
                    pdf.setDrawColor(200, 200, 200);
                    pdf.setLineWidth(0.1);
                    pdf.rect(cellX, cellY, cellWidth, cellHeight, 'S');
                  }
                },
                didParseCell: function(data: any) {
                  // Ensure proper structure for accessibility
                  if (data.cell.section === 'body') {
                    data.cell.styles.minCellHeight = 8;
                  }
                }
              });

              yPosition = (pdf as any).lastAutoTable.finalY + 3;
            }

            yPosition += 3;
          }
        }

        yPosition += 5;
      }

      yPosition += 8;
    }

    // Add page numbers to all pages
    addPageNumbers();

    // Add bookmarks/outlines for navigation (using jsPDF outline method)
    if (bookmarks.length > 0) {
      // Add outlines for each section
      bookmarks.forEach((bookmark) => {
        pdf.outline.add(null, bookmark.title, { pageNumber: bookmark.page });
      });
    }

    // Generate filename with timestamp
    const now = new Date();
    const filename = `riktlinjer-export-${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}.pdf`;
    
    pdf.save(filename);
  }

  /**
   * Strip HTML tags from string
   */
  private static stripHtml(html: string): string {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }
}
